import { defineStore } from "pinia";

import { getCompanyId, getUserId } from "@/utils/userUtils";

import fleetVehicleInspectionsApi from "@/services/api/fleetVehicleInspectionsApi";

import { useNotificationStore } from "@/stores/notificationStore";

export const useFleetVehicleInspectionStore = defineStore("fleetVehicleInspection", {
  state: () => ({
    companyId: getCompanyId(),
    currentUserId: getUserId(),
    inspections: [],
    currentInspection: null,
    selectedId: null,
    selectedRecord: null,
    listVehicleId: null,
    listWaybillId: null,
    listCompanyWide: false,
    listRequestId: 0,
    selectionRequestId: 0,
    dataIsLoading: false,
    isSubmitting: false,
    pagination: { page: 1, pages: 1, series: [], next: null, prev: null, count: 0, limit: 40 },
    vehicleInspectionsCount: null,
    countVehicleId: null,
    countRequestId: 0,
    draftRequestId: 0,
    draftIsLoading: false,
  }),

  getters: {
    selectedInspection: (state) => (String(state.selectedRecord?.id) === String(state.selectedId) ? state.selectedRecord : null) || state.inspections.find(
      (inspection) => String(inspection.id) === String(state.selectedId)
    ) || (String(state.currentInspection?.id) === String(state.selectedId) ? state.currentInspection : null),
  },

  actions: {
    // Переключает список на указанное ТС и сбрасывает данные предыдущего контекста.
    setVehicle(vehicleId) {
      if (!this.listCompanyWide && this.listWaybillId == null && String(this.listVehicleId) === String(vehicleId)) return;
      this.resetListContext();
      this.listVehicleId = vehicleId;
    },

    // Переключает список на указанный путевой лист и сбрасывает предыдущий контекст.
    setWaybill(waybillId) {
      if (!this.listCompanyWide && this.listVehicleId == null && String(this.listWaybillId) === String(waybillId)) return;
      this.resetListContext();
      this.listWaybillId = waybillId;
    },

    // Переключает store на общий список актов всех ТС компании.
    setCompanyList() {
      if (this.listCompanyWide) return;
      this.resetListContext();
      this.listCompanyWide = true;
    },

    // Очищает список, выбор и незавершённые запросы при смене владельца списка.
    resetListContext() {
      this.listRequestId += 1;
      this.selectionRequestId += 1;
      this.listVehicleId = null;
      this.listWaybillId = null;
      this.listCompanyWide = false;
      this.inspections = [];
      this.selectedId = null;
      this.selectedRecord = null;
      this.currentInspection = null;
    },

    // Подготавливает независимый счётчик актов для выбранного ТС.
    setCountVehicle(vehicleId) {
      if (String(this.countVehicleId) === String(vehicleId)) return;
      this.countRequestId += 1;
      this.countVehicleId = vehicleId;
      this.vehicleInspectionsCount = null;
    },

    // Загружает количество актов ТС, не изменяя открытый список и пагинацию.
    async fetchCount(vehicleId) {
      this.setCountVehicle(vehicleId);
      const requestId = ++this.countRequestId;
      const response = await fleetVehicleInspectionsApi.index(this.companyId, vehicleId, { page: 1, limit: 1 });
      if (requestId === this.countRequestId) {
        this.vehicleInspectionsCount = response.data.pagination_meta?.count ?? null;
      }
    },

    // Извлекает полезную нагрузку из стандартного или упрощённого ответа API.
    normalizeResponse(response) {
      return response.data.result || response.data;
    },

    // Загружает страницу актов ТС и игнорирует запоздавший ответ старого контекста.
    async fetchInspections(vehicleId, params = {}) {
      this.setVehicle(vehicleId);
      const requestId = ++this.listRequestId;
      this.dataIsLoading = true;
      try {
        const response = await fleetVehicleInspectionsApi.index(this.companyId, vehicleId, params);
        if (requestId !== this.listRequestId) return;
        this.inspections = this.normalizeResponse(response) || [];
        const meta = response.data.pagination_meta || {};
        this.pagination = {
          page: meta.page || 1,
          pages: meta.pages || 1,
          series: meta.series || [],
          next: meta.next || null,
          prev: meta.prev || null,
          count: meta.count || 0,
          limit: meta.limit || 40,
        };
        return this.inspections;
      } finally {
        if (requestId === this.listRequestId) this.dataIsLoading = false;
      }
    },

    // Загружает страницу актов путевого листа и восстанавливает выбранную запись.
    async fetchWaybillInspections(waybillId, page = 1) {
      this.setWaybill(waybillId);
      const requestId = ++this.listRequestId;
      this.dataIsLoading = true;
      try {
        const response = await fleetVehicleInspectionsApi.forCompany(this.companyId, {
          q: { waybill_id: waybillId },
          page,
        });
        if (requestId !== this.listRequestId) return;
        this.inspections = this.normalizeResponse(response) || [];
        const meta = response.data.pagination_meta || {};
        this.pagination = {
          page: meta.page || 1,
          pages: meta.pages || 1,
          series: meta.series || [],
          next: meta.next || null,
          prev: meta.prev || null,
          count: meta.count || 0,
          limit: meta.limit || 40,
        };
        if (!this.selectedInspection) {
          this.selectedId = this.inspections[0]?.id || null;
          this.selectedRecord = null;
        }
        return this.inspections;
      } finally {
        if (requestId === this.listRequestId) this.dataIsLoading = false;
      }
    },

    // Загружает страницу актов всех ТС компании с фильтрами общего списка.
    async fetchCompanyInspections(params = {}) {
      this.setCompanyList();
      const requestId = ++this.listRequestId;
      this.dataIsLoading = true;
      try {
        const response = await fleetVehicleInspectionsApi.forCompany(this.companyId, params);
        if (requestId !== this.listRequestId || !this.listCompanyWide) return;
        this.inspections = this.normalizeResponse(response) || [];
        const meta = response.data.pagination_meta || {};
        this.pagination = {
          page: meta.page || 1,
          pages: meta.pages || 1,
          series: meta.series || [],
          next: meta.next || null,
          prev: meta.prev || null,
          count: meta.count || 0,
          limit: meta.limit || 40,
        };
        this.selectedId = this.inspections[0]?.id || null;
        this.selectedRecord = null;
        return this.inspections;
      } finally {
        if (requestId === this.listRequestId) this.dataIsLoading = false;
      }
    },

    // Выбирает акт ТС и безопасно проверяет запись из параметра маршрута через список.
    async selectInspection(vehicleId, id) {
      this.setVehicle(vehicleId);
      const requestId = ++this.selectionRequestId;
      this.selectedId = id;
      this.selectedRecord = null;
      if (id == null) return null;

      const response = await fleetVehicleInspectionsApi.index(this.companyId, vehicleId, {
        "q[id]": id,
        page: 1,
        limit: 1,
      });
      if (requestId !== this.selectionRequestId || String(vehicleId) !== String(this.listVehicleId)) return null;

      this.selectedRecord = this.normalizeResponse(response)?.[0] || null;
      if (!this.selectedRecord) this.selectedId = null;

      return this.selectedRecord;
    },

    // Выбирает акт путевого листа, не подменяя открытый список одиночным ответом.
    async selectWaybillInspection(waybillId, id) {
      this.setWaybill(waybillId);
      const requestId = ++this.selectionRequestId;
      this.selectedId = id;
      this.selectedRecord = null;
      if (id == null) return null;
      if (this.inspections.some((inspection) => String(inspection.id) === String(id))) {
        return this.selectedInspection;
      }

      const response = await fleetVehicleInspectionsApi.forCompany(this.companyId, {
        q: { id, waybill_id: waybillId },
        limit: 1,
      });
      if (requestId === this.selectionRequestId && String(waybillId) === String(this.listWaybillId)) {
        this.selectedRecord = this.normalizeResponse(response)?.[0] || null;
        if (!this.selectedRecord) this.selectedId = null;
      }

      return this.selectedInspection;
    },

    // Выбирает акт из общего списка и при необходимости догружает его по ID.
    async selectCompanyInspection(id) {
      this.setCompanyList();
      const requestId = ++this.selectionRequestId;
      this.selectedId = id;
      this.selectedRecord = null;
      if (id == null) return null;
      if (this.inspections.some((inspection) => String(inspection.id) === String(id))) {
        return this.selectedInspection;
      }

      const response = await fleetVehicleInspectionsApi.forCompany(this.companyId, {
        q: { id },
        limit: 1,
      });
      if (requestId === this.selectionRequestId && this.listCompanyWide) {
        this.selectedRecord = this.normalizeResponse(response)?.[0] || null;
        if (!this.selectedRecord) this.selectedId = null;
      }

      return this.selectedInspection;
    },

    // Загружает полный акт, обновляет его в списке и делает текущим для Wizard.
    async fetchInspection(vehicleId, id) {
      this.dataIsLoading = true;
      try {
        const response = await fleetVehicleInspectionsApi.show(this.companyId, vehicleId, id);
        const inspection = this.normalizeResponse(response);
        this.upsertInspection(inspection);
        this.currentInspection = inspection;
        return inspection;
      } finally {
        this.dataIsLoading = false;
      }
    },

    // Возвращает собственный черновик пользователя или конкретный доступный черновик.
    async resumeDraft(vehicleId, draftId = null) {
      const requestId = ++this.draftRequestId;
      this.draftIsLoading = true;
      let draft;

      try {
        if (draftId != null) {
          const response = await fleetVehicleInspectionsApi.show(this.companyId, vehicleId, draftId);
          draft = this.normalizeResponse(response);
        } else {
          const response = await fleetVehicleInspectionsApi.index(this.companyId, vehicleId, {
            "q[status]": "draft",
            "q[initiator_id]": this.currentUserId,
            page: 1,
            limit: 1,
          });
          [draft] = this.normalizeResponse(response) || [];
        }
      } finally {
        if (requestId === this.draftRequestId) this.draftIsLoading = false;
      }

      // Ответ предыдущего мастера не должен заменить черновик уже выбранного ТС.
      if (requestId !== this.draftRequestId) return null;

      const ownDraft = draft && this.currentUserId != null && draft.initiator_id != null &&
        String(draft.initiator_id) === String(this.currentUserId) &&
        (draftId == null || draft.status === "draft");
      this.currentInspection = ownDraft ? draft : null;
      return this.currentInspection;
    },

    // Собирает пометки всех участников акта в единый список для отображения.
    remarksForInspection(inspection) {
      return (inspection?.participants || []).flatMap((participant) => participant.remarks || []);
    },

    // Создаёт акт с выбранной стороной участия и обновляет связанные счётчики.
    async createInspection(vehicleId, participationRole, waybillId = null) {
      this.isSubmitting = true;
      try {
        const payload = { participation_role: participationRole };
        if (waybillId) payload.waybill_id = waybillId;
        const response = await fleetVehicleInspectionsApi.create(this.companyId, vehicleId, payload);
        const inspection = this.normalizeResponse(response);
        const isNewForOpenList = String(this.listVehicleId) === String(vehicleId) &&
          !this.inspections.some((item) => String(item.id) === String(inspection?.id));
        this.currentInspection = inspection;
        this.upsertInspection(inspection);
        if (isNewForOpenList) this.pagination.count += 1;
        if (String(this.countVehicleId) === String(vehicleId) && this.vehicleInspectionsCount != null) {
          this.vehicleInspectionsCount += 1;
        }
        return inspection;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Сохраняет ответ шага и перечитывает полный актуальный акт.
    async saveAnswer(vehicleId, inspectionId, payload) {
      this.isSubmitting = true;
      try {
        await fleetVehicleInspectionsApi.answer(this.companyId, vehicleId, inspectionId, payload);
        return await this.fetchInspection(vehicleId, inspectionId);
      } finally {
        this.isSubmitting = false;
      }
    },

    // Изменяет связь путевого листа с черновиком и обновляет открытый акт.
    async updateWaybill(vehicleId, inspectionId, waybillId) {
      this.isSubmitting = true;
      try {
        const response = await fleetVehicleInspectionsApi.update(
          this.companyId,
          vehicleId,
          inspectionId,
          { waybill_id: waybillId }
        );
        const inspection = this.normalizeResponse(response);
        this.upsertInspection(inspection);
        if (String(this.currentInspection?.id) === String(inspectionId)) this.currentInspection = inspection;
        useNotificationStore().showSuccess(waybillId == null ? "Путевой лист отвязан" : "Путевой лист привязан");
        return inspection;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Завершает черновик подписью инициатора и переводит акт к ожиданию участников.
    async completeInspection(vehicleId, inspectionId, attachmentIds) {
      this.isSubmitting = true;
      try {
        const response = await fleetVehicleInspectionsApi.complete(
          this.companyId,
          vehicleId,
          inspectionId,
          { attachment_ids: attachmentIds }
        );
        const inspection = this.normalizeResponse(response);
        this.currentInspection = inspection;
        this.upsertInspection(inspection);
        useNotificationStore().showSuccess("Акт отправлен участникам");
        return inspection;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Подписывает акт текущим участником и сохраняет полученное состояние в store.
    async signInspection(vehicleId, inspectionId, attachmentIds) {
      this.isSubmitting = true;
      try {
        const response = await fleetVehicleInspectionsApi.sign(
          this.companyId,
          vehicleId,
          inspectionId,
          { attachment_ids: attachmentIds }
        );
        const inspection = this.normalizeResponse(response);
        this.upsertInspection(inspection);
        this.currentInspection = inspection;
        useNotificationStore().showSuccess("Акт подписан");
        return inspection;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Удаляет черновик и исключает его из текущего списка и состояния Wizard.
    async discardDraft(vehicleId, inspectionId) {
      this.isSubmitting = true;
      try {
        await fleetVehicleInspectionsApi.destroy(this.companyId, vehicleId, inspectionId);
        this.inspections = this.inspections.filter((item) => String(item.id) !== String(inspectionId));
        if (String(this.currentInspection?.id) === String(inspectionId)) this.currentInspection = null;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Отменяет акт и заменяет его обновлённой записью во всех представлениях store.
    async cancelInspection(vehicleId, inspectionId) {
      this.isSubmitting = true;
      try {
        const response = await fleetVehicleInspectionsApi.cancel(this.companyId, vehicleId, inspectionId);
        const inspection = this.normalizeResponse(response);
        this.upsertInspection(inspection);
        this.currentInspection = inspection;
        useNotificationStore().showSuccess("Акт отменён");
        return inspection;
      } finally {
        this.isSubmitting = false;
      }
    },

    // Добавляет или заменяет акт в открытом списке и синхронизирует выбранную запись.
    upsertInspection(inspection) {
      if (!inspection?.id) return;
      const index = this.inspections.findIndex((item) => String(item.id) === String(inspection.id));
      if (index === -1) this.inspections.unshift(inspection);
      else this.inspections.splice(index, 1, inspection);
      if (String(this.selectedId) === String(inspection.id)) this.selectedRecord = inspection;
    },

    // Очищает акт, открытый в сценарии создания или продолжения.
    resetCurrent() {
      this.draftRequestId += 1;
      this.draftIsLoading = false;
      this.currentInspection = null;
    },
  },
});
